import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Building2, Ship, Truck, ArrowRight, ArrowLeft, Check, 
    Package, FileText, MapPin, DollarSign, Users, Zap,
    Shield, Clock, BarChart3, Bot, FileCheck, Calculator,
    Loader2, CreditCard, Tag, AlertCircle, CheckCircle2,
    Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─────────────────────────────────────────────
// MOCK DATA — remove / replace with real API later
// ─────────────────────────────────────────────
const MOCK_TAKEN = {
    company_name: ['acme logistics', 'shipfast inc', 'globalfreight co', 'oceanlink ltd', 'speedcargo'],
    email:        ['admin@acme.com', 'info@shipfast.com', 'contact@globalfreight.com', 'test@test.com'],
    user_name:    ['johndoe', 'admin', 'superuser', 'janesmith', 'freightking'],
};

// Simulated API — replace this function body with real axios call
const checkAvailabilityAPI = async (field, value) => {
    // Simulate network latency (300–700ms)
    await new Promise(r => setTimeout(r, 300 + Math.random() * 400));

    const isTaken = MOCK_TAKEN[field]?.includes(value.trim().toLowerCase());

    const messages = {
        company_name: 'This company name is already registered.',
        email:        'This email is already associated with an account.',
        user_name:    'This username is taken. Try another.',
    };

    return {
        available: !isTaken,
        message: isTaken ? messages[field] : null,
    };

    /* ── REAL API (uncomment when ready) ──────────────────────────
    const res = await axios.get(`${BACKEND_URL}/api/admin/companies/check-availability`, {
        params: { field, value: value.trim() }
    });
    return res.data; // expects { available: boolean, message?: string }
    ──────────────────────────────────────────────────────────────── */
};
// ─────────────────────────────────────────────

const COMPANY_TYPES = [
    {
        id: 'BCO',
        name: 'Beneficial Cargo Owner',
        description: 'Shipper or consignee who owns the cargo',
        icon: Package,
        color: 'from-blue-500 to-blue-600',
    },
    {
        id: 'NVOCC',
        name: 'NVOCC',
        description: 'Non-Vessel Operating Common Carrier',
        icon: Ship,
        color: 'from-teal-500 to-teal-600',
    },
    {
        id: 'FreightForwarder',
        name: 'Freight Forwarder',
        description: 'Logistics service provider',
        icon: Truck,
        color: 'from-purple-500 to-purple-600',
    },
];

const MODULE_ICONS = {
    CORE_TMS:      Ship,
    RATE_QUOTE:    Calculator,
    BOOKING:       FileText,
    TRACKING:      MapPin,
    DOCUMENTATION: FileCheck,
    ACCOUNTING:    DollarSign,
    CONTROL_TOWER: BarChart3,
    AI_AGENTS:     Bot,
    CUSTOMS:       Shield,
    REPORTING:     BarChart3,
};

// ── Reusable status icon ──────────────────────
const FieldStatusIcon = ({ status }) => {
    if (status === 'checking')  return <Loader2    className="w-4 h-4 text-slate-400 animate-spin" />;
    if (status === 'available') return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
    if (status === 'taken')     return <AlertCircle  className="w-4 h-4 text-red-400" />;
    return null;
};

// ── Validated input wrapper ───────────────────
const ValidatedInput = ({ label, required, type = 'text', value, onChange, placeholder, status, errorMsg, successMsg }) => (
    <div>
        <label className="block text-sm text-slate-400 mb-1">
            {label} {required && <span className="text-teal-400">*</span>}
        </label>
        <div className="relative">
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className={`w-full px-4 py-2.5 pr-10 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500
                    focus:outline-none focus:ring-2 focus:ring-teal-500/60 transition-all duration-200
                    ${status === 'taken'     ? 'border-red-500/70 bg-red-500/5'     :
                      status === 'available' ? 'border-emerald-500/60 bg-emerald-500/5' :
                                              'border-slate-600 hover:border-slate-500'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-200">
                <FieldStatusIcon status={status} />
            </div>
        </div>

        {/* Inline feedback line */}
        <div className={`overflow-hidden transition-all duration-300 ${(status === 'available' || status === 'taken') ? 'max-h-8 mt-1.5 opacity-100' : 'max-h-0 opacity-0'}`}>
            {status === 'available' && (
                <p className="text-xs text-emerald-400 flex items-center gap-1">
                    <Check className="w-3 h-3" /> {successMsg || 'Available'}
                </p>
            )}
            {status === 'taken' && errorMsg && (
                <p className="text-xs text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {errorMsg}
                </p>
            )}
        </div>
    </div>
);


// ═══════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════
const SignupWizard = () => {
    const navigate     = useNavigate();
    const [searchParams] = useSearchParams();

    const [currentStep, setCurrentStep] = useState(1);
    const [loading,     setLoading]     = useState(false);
    const [modules,     setModules]     = useState({ core_modules: [], addon_modules: [], all_modules: [] });
    const [error,       setError]       = useState('');

    // Real-time uniqueness state
    const [fieldStatus, setFieldStatus] = useState({
        company_name: null,   // null | 'checking' | 'available' | 'taken'
        email:        null,
        user_name:    null,
    });
    const [fieldErrors, setFieldErrors] = useState({
        company_name: '',
        email:        '',
        user_name:    '',
    });
    const debounceTimers = useRef({});

    const [formData, setFormData] = useState({
        company_type:           '',
        company_name:           '',
        email:                  '',
         logo: null,
    logoPreview: '',
        phone:                  '',
        address:                '',
        city:                   '',
        state:                  '',
        country:                '',
        postal_code:            '',
        website:                '',
        nvocc_license:          '',
        user_name:              '',
        admin_password:         '',
        admin_confirm_password: '',
        admin_first_name:       '',
        admin_last_name:        '',
        selected_modules:       ['CORE_TMS'],
        coupon_code:            '',
        discount:               0,
    });

    const [signupResult,  setSignupResult]  = useState(null);
    const [submitted,     setSubmitted]     = useState(false);

    // Password visibility toggles
    const [showPassword,        setShowPassword]        = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Cancelled payment redirect
    useEffect(() => {
        const cancelled = searchParams.get('cancelled');
        if (searchParams.get('step') === 'payment' && cancelled) {
            setCurrentStep(4);
            setError('Payment was cancelled. Please try again.');
        }
    }, [searchParams]);

    useEffect(() => { fetchModules(); }, []);

    const fetchModules = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/payments/modules/pricing`);
            if (res.data.success) setModules(res.data);
        } catch (err) {
            console.error('Error fetching modules:', err);
        }
    };

    // ── Basic field updater ───────────────────
    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    // ── Debounced uniqueness check ────────────
    const checkFieldUniqueness = async (field, value) => {
        if (!value || value.trim().length < 2) {
            setFieldStatus(prev => ({ ...prev, [field]: null }));
            setFieldErrors(prev => ({ ...prev, [field]: '' }));
            return;
        }

        setFieldStatus(prev => ({ ...prev, [field]: 'checking' }));

        try {
            const result = await checkAvailabilityAPI(field, value);

            if (result.available) {
                setFieldStatus(prev => ({ ...prev, [field]: 'available' }));
                setFieldErrors(prev => ({ ...prev, [field]: '' }));
            } else {
                setFieldStatus(prev => ({ ...prev, [field]: 'taken' }));
                setFieldErrors(prev => ({ ...prev, [field]: result.message || `Already taken` }));
            }
        } catch {
            // Silently reset on network error — don't block the user
            setFieldStatus(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleUniqueField = (field, value) => {
        updateFormData(field, value);
        clearTimeout(debounceTimers.current[field]);
        debounceTimers.current[field] = setTimeout(() => {
            checkFieldUniqueness(field, value);
        }, 600);
    };

    // ── Module toggle ─────────────────────────
    const toggleModule = (moduleCode) => {
        setFormData(prev => {
            let selected = prev.selected_modules.includes(moduleCode)
                ? prev.selected_modules.filter(m => m !== moduleCode)
                : [...prev.selected_modules, moduleCode];
            if (!selected.includes('CORE_TMS')) selected.push('CORE_TMS');
            return { ...prev, selected_modules: selected };
        });
    };
    const handleLogoChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

   
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

    if (!allowedTypes.includes(file.type)) {
        alert('Only JPG, JPEG, and PNG files are allowed');
        return;
    }

    // ✅ Save file + preview
    updateFormData('logo', file);
    updateFormData('logoPreview', URL.createObjectURL(file));
};

    const calculateTotal = () => {
        let total = 0;
        formData.selected_modules.forEach(code => {
            const mod = modules.all_modules.find(m => m.module_code === code);
            if (mod) {
                total += parseFloat(mod.base_price || 0);
                if (mod.pricing_model === 'per_seat') total += parseFloat(mod.per_seat_price || 0);
            }
        });
        return total - formData.discount;
    };

    // ── Step validation ───────────────────────
    const validateStep = (step) => {
        switch (step) {
            case 1:
                if (!formData.company_type) {
                    setError('Please select a company type'); return false;
                }
                break;

            case 2: {
                // Block if still checking
                const checking = Object.values(fieldStatus).some(s => s === 'checking');
                if (checking) {
                    setError('Please wait while we verify your details…'); return false;
                }
                // Block if any unique field is taken
                const hasTaken = Object.values(fieldStatus).some(s => s === 'taken');
                if (hasTaken) {
                    setError('Please fix the highlighted fields before continuing.'); return false;
                }
                if (!formData.company_name || !formData.email || !formData.user_name ||
                    !formData.admin_password || !formData.admin_first_name || !formData.admin_last_name) {
                    setError('Please fill in all required fields'); return false;
                }
                if (formData.admin_password !== formData.admin_confirm_password) {
                    setError('Passwords do not match'); return false;
                }
                if (formData.admin_password.length < 8) {
                    setError('Password must be at least 8 characters'); return false;
                }
                if (formData.company_type === 'NVOCC' && !formData.nvocc_license) {
                    setError('NVOCC License number is required'); return false;
                }
                break;
            }

            case 3:
                if (formData.selected_modules.length === 0) {
                    setError('Please select at least one module'); return false;
                }
                break;

            default: break;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) setCurrentStep(prev => Math.min(prev + 1, 4));
    };
    const prevStep = () => { setCurrentStep(prev => Math.max(prev - 1, 1)); setError(''); };

    // ── Submit ────────────────────────────────
  // ── Submit ────────────────────────────────
const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
        /* ── REAL API (uncomment when ready) ──────────────────────────
        const signupRes = await axios.post(`${BACKEND_URL}/api/admin/companies/signup`, {
            company_name:     formData.company_name,
            company_type:     formData.company_type,
            email:            formData.email,
            phone:            formData.phone,
            address:          formData.address,
            city:             formData.city,
            state:            formData.state,
            country:          formData.country,
            postal_code:      formData.postal_code,
            website:          formData.website,
            nvocc_license:    formData.nvocc_license,
            selected_modules: formData.selected_modules,
            user_name:        formData.user_name,
            admin_password:   formData.admin_password,
            admin_first_name: formData.admin_first_name,
            admin_last_name:  formData.admin_last_name,
        });

        if (!signupRes.data.success) throw new Error(signupRes.data.detail || 'Signup failed');

        setSignupResult(signupRes.data);
        ──────────────────────────────────────────────────────────────── */

        // ── MOCK success (remove once real API is wired up) ──
        await new Promise(r => setTimeout(r, 800)); // simulate network delay
        setSignupResult({ success: true });
        // ──────────────────────────────────────────────────────

        setSubmitted(true);
    } catch (err) {
        console.error('Signup error:', err);
        setError(err.response?.data?.detail || err.message || 'An error occurred');
    }

    setLoading(false);
};

    const steps = [
        { num: 1, label: 'Account Type' },
        { num: 2, label: 'Company Details' },
        { num: 3, label: 'Select Modules' },
        { num: 4, label: 'Review & Submit' },
    ];

    // ── RENDER ────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="signup-wizard">

            {/* ── Header ── */}
       <div className="bg-slate-900/50 border-b border-slate-700/50 py-4">
    <div className="max-w-5xl mx-auto px-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" data-testid="signup-logo">
            shipez<span className="text-teal-400">.ai</span>
        </h1>
        <div className="flex items-center gap-3">
            <span className="text-slate-500 text-sm">Already have an account?</span>
            <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 font-medium transition-colors"
            >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to Login
            </button>
        </div>
    </div>
</div>

            <div className="max-w-5xl mx-auto px-4 py-8">

                {/* ── Progress bar ── */}
                <div className="flex items-center justify-between mb-8" data-testid="signup-progress">
                    {steps.map((step, idx) => (
                        <React.Fragment key={step.num}>
                            <div className="flex items-center">
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                                        currentStep >= step.num ? 'bg-teal-500 text-white' : 'bg-slate-700 text-slate-400'
                                    }`}
                                    data-testid={`signup-step-${step.num}`}
                                >
                                    {currentStep > step.num ? <Check className="w-5 h-5" /> : step.num}
                                </div>
                                <span className={`ml-3 text-sm font-medium hidden sm:block ${currentStep >= step.num ? 'text-white' : 'text-slate-500'}`}>
                                    {step.label}
                                </span>
                            </div>
                            {idx < steps.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-4 transition-all duration-500 ${currentStep > step.num ? 'bg-teal-500' : 'bg-slate-700'}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* ── Global error banner ── */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 animate-pulse-once" data-testid="signup-error">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <span className="text-red-400 text-sm">{error}</span>
                    </div>
                )}

                {/* ── Step content card ── */}
                <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 md:p-8" data-testid={`signup-step-content-${currentStep}`}>

                    {/* ════════ STEP 1: Company Type ════════ */}
                    {currentStep === 1 && (
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-2">Select Your Account Type</h2>
                            <p className="text-slate-400 mb-8">Choose the type that best describes your business</p>

                            <div className="grid md:grid-cols-3 gap-4">
                                {COMPANY_TYPES.map(type => (
                                    <div
                                        key={type.id}
                                        onClick={() => updateFormData('company_type', type.id)}
                                        className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                            formData.company_type === type.id
                                                ? 'border-teal-500 bg-teal-500/10 scale-[1.02]'
                                                : 'border-slate-600 hover:border-slate-500 bg-slate-700/30 hover:scale-[1.01]'
                                        }`}
                                    >
                                        {formData.company_type === type.id && (
                                            <div className="absolute top-3 right-3">
                                                <CheckCircle2 className="w-6 h-6 text-teal-500" />
                                            </div>
                                        )}
                                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center mb-4`}>
                                            <type.icon className="w-7 h-7 text-white" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-white mb-2">{type.name}</h3>
                                        <p className="text-sm text-slate-400">{type.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ════════ STEP 2: Company Details ════════ */}
                    {currentStep === 2 && (
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-2">Company & Admin Details</h2>
                            <p className="text-slate-400 mb-8">Enter your company information and create your admin account</p>

                            <div className="grid md:grid-cols-2 gap-8">

                                {/* ── Left: Company Info ── */}
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        <Building2 className="w-5 h-5 text-teal-500" /> Company Information
                                    </h3>
                                    <div className="space-y-4">

                                        {/* Company Name — uniqueness check */}
                                        <ValidatedInput
                                            label="Company Name"
                                            required
                                            value={formData.company_name}
                                            onChange={e => handleUniqueField('company_name', e.target.value)}
                                            placeholder="Acme Logistics Inc."
                                            status={fieldStatus.company_name}
                                            errorMsg={fieldErrors.company_name}
                                            successMsg="Company name is available"
                                        />

                                        {/* Company Email — uniqueness check */}
                                        <ValidatedInput
                                            label="Company Email"
                                            required
                                            type="email"
                                            value={formData.email}
                                            onChange={e => handleUniqueField('email', e.target.value)}
                                            placeholder="info@company.com"
                                            status={fieldStatus.email}
                                            errorMsg={fieldErrors.email}
                                            successMsg="Email is available"
                                        />
                                      <div>
    <label className="block text-sm text-slate-400 mb-1">
        Company Logo <span className="text-red-400">*</span>
    </label>

    <input
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={handleLogoChange}
        className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-teal-500 file:text-white hover:file:bg-teal-600"
        required
    />

    {/* Preview */}
    {formData.logoPreview && (
        <img
            src={formData.logoPreview}
            alt="Company Logo Preview"
            className="mt-3 h-16 w-auto rounded-md border border-slate-600"
        />
    )}
</div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">Phone</label>
                                                <input
                                                    type="tel"
                                                    value={formData.phone}
                                                    onChange={e => updateFormData('phone', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="+1-555-123-4567"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">Website</label>
                                                <input
                                                    type="url"
                                                    value={formData.website}
                                                    onChange={e => updateFormData('website', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="www.company.com"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm text-slate-400 mb-1">Address</label>
                                            <input
                                                type="text"
                                                value={formData.address}
                                                onChange={e => updateFormData('address', e.target.value)}
                                                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                placeholder="123 Business Ave"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">City</label>
                                                <input
                                                    type="text"
                                                    value={formData.city}
                                                    onChange={e => updateFormData('city', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="Los Angeles"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">State</label>
                                                <input
                                                    type="text"
                                                    value={formData.state}
                                                    onChange={e => updateFormData('state', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="California"
                                                />
                                            </div>
                                             <div>
                                                <label className="block text-sm text-slate-400 mb-1">Country</label>
                                                <input
                                                    type="text"
                                                    value={formData.country}
                                                    onChange={e => updateFormData('country', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="United States"
                                                />
                                            </div>
                                        </div>

                                        {/* NVOCC License — only shown for NVOCC company type */}
                                        {formData.company_type === 'NVOCC' && (
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">
                                                    NVOCC License <span className="text-teal-400">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.nvocc_license}
                                                    onChange={e => updateFormData('nvocc_license', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="FMC License #"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* ── Right: Admin Account ── */}
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                        <Users className="w-5 h-5 text-teal-500" /> Admin Account
                                    </h3>
                                    <div className="space-y-4">

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">
                                                    First Name <span className="text-teal-400">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.admin_first_name}
                                                    onChange={e => updateFormData('admin_first_name', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="John"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-slate-400 mb-1">
                                                    Last Name <span className="text-teal-400">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.admin_last_name}
                                                    onChange={e => updateFormData('admin_last_name', e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="Doe"
                                                />
                                            </div>
                                        </div>

                                        {/* Username — uniqueness check */}
                                        <ValidatedInput
                                            label="Username"
                                            required
                                            value={formData.user_name}
                                            onChange={e => handleUniqueField('user_name', e.target.value)}
                                            placeholder="johndoe"
                                            status={fieldStatus.user_name}
                                            errorMsg={fieldErrors.user_name}
                                            successMsg="Username is available"
                                        />

                                        <div>
                                            <label className="block text-sm text-slate-400 mb-1">
                                                Password <span className="text-teal-400">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={formData.admin_password}
                                                    onChange={e => updateFormData('admin_password', e.target.value)}
                                                    className="w-full px-4 py-2.5 pr-10 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60"
                                                    placeholder="Min 8 characters"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(p => !p)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors focus:outline-none"
                                                    tabIndex={-1}
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                >
                                                    {showPassword
                                                        ? <EyeOff className="w-4 h-4" />
                                                        : <Eye    className="w-4 h-4" />
                                                    }
                                                </button>
                                            </div>
                                            {/* Strength hint */}
                                            {formData.admin_password && formData.admin_password.length < 8 && (
                                                <p className="text-xs text-amber-400 mt-1.5 flex items-center gap-1">
                                                    <AlertCircle className="w-3 h-3" /> At least 8 characters required
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm text-slate-400 mb-1">
                                                Confirm Password <span className="text-teal-400">*</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={formData.admin_confirm_password}
                                                    onChange={e => updateFormData('admin_confirm_password', e.target.value)}
                                                    className={`w-full px-4 py-2.5 pr-10 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/60 transition-colors ${
                                                        formData.admin_confirm_password && formData.admin_password !== formData.admin_confirm_password
                                                            ? 'border-red-500/70 bg-red-500/5'
                                                            : formData.admin_confirm_password && formData.admin_password === formData.admin_confirm_password
                                                            ? 'border-emerald-500/60 bg-emerald-500/5'
                                                            : 'border-slate-600'
                                                    }`}
                                                    placeholder="Confirm password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(p => !p)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors focus:outline-none"
                                                    tabIndex={-1}
                                                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                                                >
                                                    {showConfirmPassword
                                                        ? <EyeOff className="w-4 h-4" />
                                                        : <Eye    className="w-4 h-4" />
                                                    }
                                                </button>
                                            </div>
                                            {/* Password match feedback */}
                                            {formData.admin_confirm_password && (
                                                <p className={`text-xs mt-1.5 flex items-center gap-1 ${
                                                    formData.admin_password === formData.admin_confirm_password
                                                        ? 'text-emerald-400' : 'text-red-400'
                                                }`}>
                                                    {formData.admin_password === formData.admin_confirm_password
                                                        ? <><Check className="w-3 h-3" /> Passwords match</>
                                                        : <><AlertCircle className="w-3 h-3" /> Passwords do not match</>
                                                    }
                                                </p>
                                            )}
                                        </div>

                                     
                                       
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ════════ STEP 3: Module Selection ════════ */}
                    {currentStep === 3 && (
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-2">Select Your Modules</h2>
                            <p className="text-slate-400 mb-8">Choose the modules you need. Core TMS is required.</p>

                            <div className="space-y-6">
                                {/* Core modules */}
                                <div>
                                    <h3 className="text-sm font-semibold text-teal-400 uppercase tracking-wider mb-4">Core Modules</h3>
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {modules.all_modules.filter(m => ['CORE_TMS','BOOKING','RATE_QUOTE','TRACKING'].includes(m.module_code)).map(module => {
                                            const Icon = MODULE_ICONS[module.module_code] || Package;
                                            const isSelected = formData.selected_modules.includes(module.module_code);
                                            const isRequired = module.module_code === 'CORE_TMS';
                                            return (
                                                <div
                                                    key={module.module_code}
                                                    onClick={() => !isRequired && toggleModule(module.module_code)}
                                                    className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${isRequired ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02]'} ${
                                                        isSelected ? 'border-teal-500 bg-teal-500/10' : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                                                    }`}
                                                >
                                                    {isRequired && <span className="absolute top-2 right-2 text-[10px] bg-teal-500 text-white px-2 py-0.5 rounded">Required</span>}
                                                    {isSelected && !isRequired && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-teal-500" />}
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-slate-600/50 flex items-center justify-center">
                                                            <Icon className="w-5 h-5 text-teal-400" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-white text-sm">{module.module_name}</h4>
                                                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{module.description}</p>
                                                            <div className="mt-2 flex items-baseline gap-1">
                                                                <span className="text-lg font-bold text-white">${module.base_price}</span>
                                                                <span className="text-xs text-slate-400">/mo</span>
                                                                {module.per_seat_price > 0 && <span className="text-xs text-slate-500 ml-1">+ ${module.per_seat_price}/seat</span>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Add-on modules */}
                                <div>
                                    <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-4">Add-on Modules</h3>
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {modules.all_modules.filter(m => !['CORE_TMS','BOOKING','RATE_QUOTE','TRACKING'].includes(m.module_code)).map(module => {
                                            const Icon = MODULE_ICONS[module.module_code] || Package;
                                            const isSelected = formData.selected_modules.includes(module.module_code);
                                            return (
                                                <div
                                                    key={module.module_code}
                                                    onClick={() => toggleModule(module.module_code)}
                                                    className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
                                                        isSelected ? 'border-purple-500 bg-purple-500/10' : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                                                    }`}
                                                >
                                                    {isSelected && <CheckCircle2 className="absolute top-2 right-2 w-5 h-5 text-purple-500" />}
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-10 h-10 rounded-lg bg-slate-600/50 flex items-center justify-center">
                                                            <Icon className="w-5 h-5 text-purple-400" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-white text-sm">{module.module_name}</h4>
                                                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{module.description}</p>
                                                            <div className="mt-2 flex items-baseline gap-1">
                                                                <span className="text-lg font-bold text-white">${module.base_price}</span>
                                                                <span className="text-xs text-slate-400">/mo</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Running total */}
                            <div className="mt-6 p-4 bg-slate-700/30 rounded-xl border border-slate-600 flex items-center justify-between">
                                <div>
                                    <span className="text-slate-400">Selected Modules:</span>
                                    <span className="ml-2 text-white font-semibold">{formData.selected_modules.length}</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-slate-400">Estimated Monthly:</span>
                                    <span className="ml-2 text-2xl font-bold text-teal-400">${calculateTotal().toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ════════ STEP 4: Review & Submit ════════ */}
                    {currentStep === 4 && !submitted && (
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-1">Review Your Details</h2>
                            <p className="text-slate-400 mb-8">Everything look good? Submit your application and our team will be in touch.</p>

                            <div className="grid md:grid-cols-3 gap-5">

                                {/* ── Card: Company ── */}
                                <div className="p-5 bg-slate-700/30 border border-slate-600/60 rounded-2xl space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                            <Building2 className="w-4 h-4 text-blue-400" />
                                        </div>
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Company</h3>
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold text-base leading-tight">{formData.company_name}</p>
                                        <span className="inline-block mt-1.5 px-2 py-0.5 text-[10px] font-medium rounded-full bg-teal-500/15 text-teal-400 border border-teal-500/20">
                                            {formData.company_type}
                                        </span>
                                    </div>
                                    <div className="pt-2 border-t border-slate-600/50 space-y-1.5">
                                        {formData.email && (
                                            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                                {formData.email}
                                            </p>
                                        )}
                                        {formData.phone && (
                                            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                                {formData.phone}
                                            </p>
                                        )}
                                        {formData.website && (
                                            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                                {formData.website}
                                            </p>
                                        )}
                                        {(formData.city || formData.state) && (
                                            <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                                {[formData.city, formData.state, formData.country].filter(Boolean).join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* ── Card: Admin Account ── */}
                                <div className="p-5 bg-slate-700/30 border border-slate-600/60 rounded-2xl space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                            <Users className="w-4 h-4 text-purple-400" />
                                        </div>
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Admin Account</h3>
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold text-base">{formData.admin_first_name} {formData.admin_last_name}</p>
                                        <p className="text-xs text-teal-400 mt-0.5">@{formData.user_name}</p>
                                    </div>
                                    <div className="pt-2 border-t border-slate-600/50">
                                        <p className="text-xs text-slate-400 flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                            Password set & confirmed
                                        </p>
                                        {formData.nvocc_license && (
                                            <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-1.5">
                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                                License: {formData.nvocc_license}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* ── Card: Modules ── */}
                                <div className="p-5 bg-slate-700/30 border border-slate-600/60 rounded-2xl space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-7 h-7 rounded-lg bg-teal-500/20 flex items-center justify-center">
                                            <Package className="w-4 h-4 text-teal-400" />
                                        </div>
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                                            Modules <span className="text-slate-500 normal-case font-normal">({formData.selected_modules.length})</span>
                                        </h3>
                                    </div>
                                    <div className="space-y-2">
                                        {formData.selected_modules.map(code => {
                                            const mod = modules.all_modules.find(m => m.module_code === code);
                                            const Icon = MODULE_ICONS[code] || Package;
                                            return (
                                                <div key={code} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Icon className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
                                                        <span className="text-white text-xs">{mod?.module_name || code}</span>
                                                    </div>
                                                    {mod && (
                                                        <span className="text-slate-400 text-xs">${mod.base_price}/mo</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {modules.all_modules.length > 0 && (
                                        <div className="pt-2 border-t border-slate-600/50 flex justify-between items-baseline">
                                            <span className="text-xs text-slate-400">Est. monthly</span>
                                            <span className="text-base font-bold text-teal-400">${calculateTotal().toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── Info notice ── */}
                            <div className="mt-6 p-4 bg-blue-500/8 border border-blue-500/20 rounded-xl flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Shield className="w-4 h-4 text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-sm text-white font-medium">What happens next?</p>
                                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                                        Once you submit, our onboarding team will review your application, verify your details, and reach out within <span className="text-white">1–2 business days</span> to get your account fully activated and walk you through the platform.
                                    </p>
                                </div>
                            </div>

                            {/* ── Submit button ── */}
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    data-testid="signup-submit-button"
                                    className="px-8 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-teal-500/20 flex items-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
                                    ) : (
                                        <><Check className="w-5 h-5" /> Submit Application</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ════════ SUCCESS SCREEN ════════ */}
                    {currentStep === 4 && submitted && (
                        <div className="py-10 flex flex-col items-center text-center">
                            {/* Animated check circle */}
                            <div className="relative mb-8">
                                <div className="w-28 h-28 rounded-full bg-teal-500/10 border-2 border-teal-500/30 flex items-center justify-center">
                                    <div className="w-20 h-20 rounded-full bg-teal-500/20 border-2 border-teal-500/50 flex items-center justify-center">
                                        <CheckCircle2 className="w-12 h-12 text-teal-400" strokeWidth={1.5} />
                                    </div>
                                </div>
                                {/* Subtle pulse ring */}
                                <div className="absolute inset-0 rounded-full border-2 border-teal-500/20 animate-ping" style={{ animationDuration: '2.5s' }} />
                            </div>

                            <h2 className="text-3xl font-bold text-white mb-3">
                                Application Submitted!
                            </h2>
                            <p className="text-slate-300 text-base max-w-md leading-relaxed mb-2">
                                Thank you, <span className="text-teal-400 font-semibold">{formData.admin_first_name}</span>. Your application for <span className="text-white font-semibold">{formData.company_name}</span> has been received.
                            </p>
                            <p className="text-slate-400 text-sm max-w-md leading-relaxed mb-8">
                                Our onboarding team will review your details and reach out to <span className="text-slate-300">{formData.email}</span> within <span className="text-white font-medium">1–2 business days</span> to verify your account and guide you through getting started on shipez.ai.
                            </p>

                            {/* Info tiles */}
                            <div className="grid grid-cols-3 gap-4 w-full max-w-lg mb-8">
                                <div className="p-4 bg-slate-700/30 border border-slate-600/50 rounded-xl text-center">
                                    <Clock className="w-5 h-5 text-teal-400 mx-auto mb-2" />
                                    <p className="text-white text-xs font-semibold">1–2 Business Days</p>
                                    <p className="text-slate-500 text-[10px] mt-0.5">Response time</p>
                                </div>
                                <div className="p-4 bg-slate-700/30 border border-slate-600/50 rounded-xl text-center">
                                    <Shield className="w-5 h-5 text-teal-400 mx-auto mb-2" />
                                    <p className="text-white text-xs font-semibold">Secure Review</p>
                                    <p className="text-slate-500 text-[10px] mt-0.5">Data is protected</p>
                                </div>
                                <div className="p-4 bg-slate-700/30 border border-slate-600/50 rounded-xl text-center">
                                    <Zap className="w-5 h-5 text-teal-400 mx-auto mb-2" />
                                    <p className="text-white text-xs font-semibold">Fast Onboarding</p>
                                    <p className="text-slate-500 text-[10px] mt-0.5">Guided setup</p>
                                </div>
                            </div>

                            <p className="text-xs text-slate-500">
                                Didn't receive a confirmation? Check your spam folder or{' '}
                                <a href="mailto:support@shipez.ai" className="text-teal-400 hover:underline">contact support</a>.
                            </p>
                        </div>
                    )}

                    {/* ── Navigation (hidden on success screen) ── */}
                    {!submitted && (
                    <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 1}
                            className="px-6 py-2.5 text-slate-400 hover:text-white transition-colors flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>

                        {currentStep < 4 && (
                            <button
                                onClick={nextStep}
                                disabled={
                                    currentStep === 2 &&
                                    (Object.values(fieldStatus).some(s => s === 'checking' || s === 'taken'))
                                }
                                className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                            >
                                {currentStep === 2 && Object.values(fieldStatus).some(s => s === 'checking') ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Checking...</>
                                ) : (
                                    <>Continue <ArrowRight className="w-4 h-4" /></>
                                )}
                            </button>
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignupWizard;